import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import VerdictBadge from "@/components/VerdictBadge";
import { ArrowUpRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1718766304636-cb9309953a55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxnaGFuYSUyMGFjY3JhJTIwY2l0eXNjYXBlfGVufDB8fHx8MTc4MzgzNjcwNnww&ixlib=rb-4.1.0&q=85";

function routeFor(item) {
  return `/${item.type.replace("_", "-")}/${item.slug}`;
}

export default function Home() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ghana, setGhana] = useState([]);

  useEffect(() => {
    api.get("/public/content").then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
    api.get("/public/content", { params: { category: "Ghana" } }).then((r) => setGhana(r.data)).catch(() => setGhana([]));
  }, []);

  const featured = items[0];
  const rest = items.slice(1);

  return (
    <div>
      <title>Be-Apuo Media Intelligence — Research, Fact-Checking & Analysis</title>
      <meta name="description" content="Independent research, fact-checking and analysis on Ghana, Africa, the diaspora, the United States and global affairs." />

      {/* Masthead */}
      <section className="border-b border-ink/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="label-caps text-clay">Independent · Evidence-Led · Ghana &amp; Beyond</div>
          <h1 className="mt-4 font-editorial font-black tracking-tight hero-display max-w-4xl text-ink">
            The record, verified. Journalism built on sources, claims and evidence.
          </h1>
        </div>
      </section>

      {/* Featured */}
      {loading ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-ink/55">Loading published work…</div>
      ) : items.length === 0 ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="border border-ink/15 bg-white p-12 text-center">
            <h2 className="font-editorial text-2xl font-bold text-ink">No published stories yet</h2>
            <p className="mt-3 text-ink/55">Approved research and fact-checks will appear here once published from the Command Center.</p>
          </div>
        </div>
      ) : (
        <>
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <Link
              to={routeFor(featured)}
              data-testid="featured-content"
              className="group grid lg:grid-cols-2 gap-10 items-center"
            >
              <div className="order-2 lg:order-1">
                <div className="flex items-center gap-3">
                  <span className="label-caps text-clay">{featured.label}</span>
                  {featured.verdict && <VerdictBadge verdict={featured.verdict} />}
                </div>
                <h2 className="mt-4 font-editorial font-black text-3xl sm:text-4xl leading-tight tracking-tight text-ink group-hover:underline decoration-2 underline-offset-4">
                  {featured.title}
                </h2>
                <p className="mt-4 text-lg text-ink/75 leading-relaxed">{featured.summary || featured.subtitle}</p>
                <div className="mt-5 text-sm text-ink/55">
                  By {featured.author} · {new Date(featured.published_at).toLocaleDateString()}
                </div>
              </div>
              <div className="order-1 lg:order-2 overflow-hidden">
                <img
                  src={HERO_IMG}
                  alt="Accra cityscape"
                  className="w-full h-72 lg:h-96 object-cover grayscale-[0.15] group-hover:scale-[1.02] transition-transform duration-500"
                />
              </div>
            </Link>
          </section>

          {/* Grid */}
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
            <div className="flex items-center justify-between border-b-2 border-ink pb-3 mb-8">
              <h3 className="font-editorial font-bold text-xl text-ink">Latest</h3>
              <span className="label-caps text-ink/55">{rest.length} stories</span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
              {rest.map((item) => (
                <Link
                  key={item.id}
                  to={routeFor(item)}
                  data-testid={`content-card-${item.slug}`}
                  className="group block border-t border-ink/80 pt-4"
                >
                  <div className="flex items-center gap-2">
                    <span className="label-caps text-clay">{item.label}</span>
                    {item.verdict && <VerdictBadge verdict={item.verdict} />}
                  </div>
                  <h4 className="mt-3 font-editorial font-bold text-xl leading-snug text-ink group-hover:underline decoration-2 underline-offset-4">
                    {item.title}
                  </h4>
                  <p className="mt-2 text-sm text-ink/70 line-clamp-3">{item.summary || item.subtitle}</p>
                  <div className="mt-4 flex items-center gap-1 text-xs text-heritage font-medium">
                    Read <ArrowUpRight className="h-3 w-3" />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </>
      )}

      {/* Ghana Intelligence section */}
      <section className="bg-ink text-white" data-testid="ghana-intelligence-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="label-caps text-gold">Ghana Intelligence</div>
              <h2 className="mt-2 font-editorial font-black text-3xl sm:text-4xl tracking-tight">Ghana, examined with evidence</h2>
            </div>
            <Link to="/category/ghana" className="text-sm border border-white/30 px-4 py-2 hover:bg-white hover:text-ink transition-colors">View All Ghana Intelligence →</Link>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {["Ghana Fact Checks", "Ghana Politics", "Economy & Public Policy", "Infrastructure", "Know Your Law – Ghana"].map((f) => (
              <span key={f} className="text-xs border border-white/20 px-3 py-1.5 text-white/80">{f}</span>
            ))}
          </div>
          <div className="mt-8 grid md:grid-cols-3 gap-8">
            {ghana.length === 0 ? (
              <p className="text-white/50 text-sm">Ghana coverage will appear here as it is published.</p>
            ) : ghana.slice(0, 3).map((g) => (
              <Link key={g.id} to={routeFor(g)} className="group block border-t border-gold/50 pt-4">
                <div className="flex items-center gap-2">
                  <span className="label-caps text-gold">{g.label}</span>
                  {g.verdict && <VerdictBadge verdict={g.verdict} />}
                </div>
                <h4 className="mt-2 font-editorial font-bold text-lg group-hover:underline decoration-2 underline-offset-4">{g.title}</h4>
                <p className="mt-1 text-sm text-white/60 line-clamp-2">{g.summary}</p>
                {g.published_at && <div className="mt-2 text-xs text-white/40">{new Date(g.published_at).toLocaleDateString()}</div>}
              </Link>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-4 border-t border-white/10 pt-8">
            <div className="flex-1 min-w-[200px]">
              <div className="font-editorial font-bold text-lg">Ghana Intelligence newsletter</div>
              <p className="text-sm text-white/60">Fact-checks and analysis on Ghana, in your inbox.</p>
            </div>
            <Link to="/#newsletter" data-testid="ghana-newsletter-cta" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid=newsletter-email-input]')?.scrollIntoView({ behavior: "smooth" }); }}
              className="bg-gold text-ink font-semibold px-5 py-2.5 text-sm hover:bg-gold/90 transition-colors">Subscribe to Ghana Intelligence</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
